import React, { useMemo, useState } from 'react';
import { Dropdown, Form, useApi } from '@axiom/ui';

import { TagType } from '../../enums/generated/tag-type';
import { TagApi } from '../../api/generated/tag-api';
import type { ReadLawSchoolsResponseEntity } from '../../api/generated/entities/read-law-schools-response-entity';

import { AdminModalSearchListDetail } from './AdminModalSearchListDetail';
import { AdminModalSearchList } from './AdminModalSearchList';
import { AdminModalDetail } from './AdminModalDetail';
import { AdminModalLawSchoolsForm } from './AdminModalLawSchoolsForm';
import { useAdminModalLawSchoolsData } from './useAdminModalLawSchoolsData';
import { AdminModalLawSchoolsRankFilterFormSchema } from './admin-modal-law-schools-rank-filter-form-schema';
import type { AdminModalLawSchoolsFormValues } from './admin-modal-law-schools-form-schema';

type SelectedSchool = {
  id: string | null;
  initialValues: AdminModalLawSchoolsFormValues;
};

export const AdminModalLawSchoolsView = () => {
  const [{ data: tags }] = useApi(TagApi.readTags());
  const lawSchoolRanks = useMemo(
    () =>
      tags
        .filter(tag => tag.type === TagType.LawSchoolRank)
        .map(tag => ({ label: tag.name ?? '', value: tag.id })),
    [tags]
  );

  const { results, hasMore, loadMore, onSearchChange, setRankFilter } =
    useAdminModalLawSchoolsData();

  const [selected, setSelected] = useState<SelectedSchool | null>(null);

  const onSelect = (lawSchool: ReadLawSchoolsResponseEntity) => {
    setSelected({
      id: lawSchool.id,
      initialValues: {
        name: lawSchool.name ?? '',
        tags: lawSchool.tags[0]?.id ?? null,
      },
    });
  };

  const onAdd = () => {
    const unranked = lawSchoolRanks.find(({ label }) =>
      /unranked/i.test(label)
    );
    setSelected({
      id: null,
      initialValues: { name: '', tags: unranked?.value ?? null },
    });
  };

  return (
    <AdminModalSearchListDetail
      name="MANAGE_LAW_SCHOOLS"
      list={
        <AdminModalSearchList<ReadLawSchoolsResponseEntity>
          name="SEARCH_LIST"
          searchName="LAW_SCHOOL_SEARCH"
          searchPlaceholder="Search schools"
          onSearchChange={onSearchChange}
          items={results.filter(school => !!school.name?.trim())}
          selectedId={selected?.id ?? null}
          onSelect={onSelect}
          renderRow={school => ({ content: school.name })}
          addControl={{
            kind: 'button',
            label: 'Add School',
            name: 'ADD_SCHOOL',
            onAdd,
          }}
          filterSlot={
            <Form
              name="LAW_SCHOOLS_FILTER"
              schema={AdminModalLawSchoolsRankFilterFormSchema}
              initialValues={{ rank: undefined }}
              submitOnChange
              onSubmit={(_changedData, _actions, values) => {
                setRankFilter(values.rank ?? undefined);
              }}
            >
              {() => (
                <Dropdown
                  name="rank"
                  label="School ranking"
                  placeholder="Filter by school ranking"
                  options={lawSchoolRanks}
                  displayKey="label"
                  valueKey="value"
                />
              )}
            </Form>
          }
          pagination={{ hasMore, onLoadMore: loadMore }}
        />
      }
      detail={
        <AdminModalDetail
          emptyText="Select a school."
          emptyName="NO_SCHOOL_SELECTED"
        >
          {selected ? (
            <AdminModalLawSchoolsForm
              id={selected.id}
              key={selected.id ?? null}
              initialValues={selected.initialValues}
              lawSchoolRanks={lawSchoolRanks}
              onCancel={() => {
                setSelected(null);
              }}
            />
          ) : null}
        </AdminModalDetail>
      }
    />
  );
};
